/**
 * One course builder run (docs/plans/lms-course-builder-plan.md 8.2 step 4,
 * 6.1, 6.12, 8.5, 9.4, 18.2 Q7, 18.5; WP-B30), the route element at
 * /lms/manage/catalog/runs/:runId, and the source link page at
 * /lms/manage/catalog/sources/:sourceId (7.3).
 *
 * The run page polls GET /admin/builder/runs/:id every 5 seconds while the
 * run is live and the page is visible (4.10), and shows: the stage list with
 * the current stage marked, "Section n of N", the repairs done, the spend so
 * far beside the estimate, the elapsed time, the builder's own progress line
 * and warnings, Cancel (a confirm; a queued run is cancelled at once, an
 * active one stops before its next AI call), the sources with their reading
 * outcome (web links with their download outcome, 18.2 Q7), Open for each
 * file (a presigned URL in a new tab) and, once the run has ended, Withdraw
 * this file (9.4, with a reason). The outcome: "Open the draft" (the editor),
 * the lint count when the draft still has problems, or the failure in plain
 * words with Retry from where it stopped (6.12) and Open the partial draft
 * when one was written. A toast marks the end of a run the page watched;
 * there is no notification (Q10).
 *
 * The source link page is what a built course's sources list points at for
 * an uploaded file (7.3): it asks GET /admin/builder/sources/:id/url and
 * sends the browser to the presigned address; a withdrawn file answers 404
 * and the page says so.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle2, Circle, ExternalLink, FileWarning, Loader2, PencilLine, RotateCcw, Sparkles, XCircle } from 'lucide-react';
import ConfirmModal from '../../../../../ui/ConfirmModal';
import Modal from '../../../../../ui/Modal';
import PageHeader from '../../../../../ui/PageHeader';
import { useToast } from '../../../../../ui/Toast';
import { dangerBtnCls, outlineBtnCls, primaryBtnCls, secondaryBtnCls } from '../../../../../ui/styles';
import {
  cancelBuilderRun,
  formatCents,
  getBuilderSourceUrl,
  isTerminalRunStatus,
  readBuilderError,
  startBuilderRun,
  withdrawBuilderSource,
} from '../../../../api/builder';
import type { BuilderRun, BuilderSource, BuilderSourceLink } from '../../../../api/builder';
import { openPendingTab } from '../../../../components/player/DoneStage';
import { noticeCls, pillCls } from '../../../../components/player/styles';
import { TextArea } from '../fields';
import { catalogPaths } from '../model';
import { cardCls, hintCls, linkCls, microLabelCls, subheadingCls } from '../styles';
import {
  canRetryRun,
  elapsedMs,
  explainError,
  fileSizeLabel,
  formatElapsed,
  sectionLine,
  sectionPercent,
  stageRows,
  statusPill,
} from './runModel';
import type { StageRow } from './runModel';
import { useBuildRun } from './useBuildRun';

const MODE_WORDS: Record<string, string> = {
  new: 'A new course from documents',
  extend: 'New material added to a course',
  section: 'One section rewritten',
};

function StageIcon({ state }: { state: StageRow['state'] }) {
  switch (state) {
    case 'done':
      return <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" aria-hidden="true" />;
    case 'current':
      return <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />;
    case 'failed':
      return <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" aria-hidden="true" />;
    case 'stopped':
      return <XCircle className="h-4 w-4 text-gray-500 dark:text-gray-400" aria-hidden="true" />;
    default:
      return <Circle className="h-4 w-4 text-gray-300 dark:text-gray-600" aria-hidden="true" />;
  }
}

const STATE_WORDS: Record<StageRow['state'], string> = {
  done: 'done',
  current: 'in progress',
  pending: 'waiting',
  failed: 'failed here',
  stopped: 'stopped here',
};

function StageList({ run }: { run: BuilderRun }) {
  const rows = stageRows(run);
  return (
    <ol className="space-y-1" aria-label="Stages" data-testid="lms-builder-stages">
      {rows.map((row) => (
        <li
          key={row.stage}
          className={`flex items-center gap-2 rounded-lg px-2 py-1 text-sm ${
            row.state === 'current'
              ? 'bg-primary-50 font-medium text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
              : row.state === 'failed'
                ? 'bg-red-50 font-medium text-red-700 dark:bg-red-900/20 dark:text-red-400'
                : row.state === 'pending'
                  ? 'text-gray-500 dark:text-gray-400'
                  : 'text-gray-800 dark:text-gray-200'
          }`}
          data-testid={`lms-builder-stage-${row.stage}`}
          data-state={row.state}
          aria-current={row.state === 'current' ? 'step' : undefined}
        >
          <StageIcon state={row.state} />
          <span>{row.label}</span>
          <span className="sr-only">{`(${STATE_WORDS[row.state]})`}</span>
        </li>
      ))}
    </ol>
  );
}

/** Whether a web link of the run has been fetched into a source row (18.2 Q7). */
function linkOutcome(link: BuilderSourceLink, sources: BuilderSource[], run: BuilderRun): { label: string; tone: 'green' | 'red' | 'gray' } {
  const row = sources.find((s) => s.public_url === link.url && (s.extraction_note || '').length > 0) ?? sources.find((s) => s.public_url === link.url);
  if (row) {
    if (row.extraction_status === 'refused' || row.extraction_status === 'failed') return { label: 'Downloaded, not readable', tone: 'red' };
    return { label: 'Downloaded', tone: 'green' };
  }
  if (run.error_code === 'LMS_BUILDER_LINK_FETCH_FAILED' && (run.error || '').includes(link.url)) {
    return { label: 'Could not be downloaded', tone: 'red' };
  }
  if (isTerminalRunStatus(run.status)) return { label: 'Not downloaded', tone: 'gray' };
  return { label: 'Waiting to be downloaded', tone: 'gray' };
}

export function RunPage() {
  const { runId } = useParams();
  const { toast } = useToast();
  const { detail, error, loading, refresh } = useBuildRun(runId);
  const [now, setNow] = useState(() => Date.now());
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [busy, setBusy] = useState<'cancel' | 'retry' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState<BuilderSource | null>(null);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [linkFor, setLinkFor] = useState<{ id: string; url: string } | null>(null);
  const lastStatus = useRef<string | null>(null);

  const run = detail?.run ?? null;
  const live = run ? !isTerminalRunStatus(run.status) : false;

  // The elapsed clock ticks once a second while the run is live.
  useEffect(() => {
    if (!live) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [live]);

  // A toast when a run the page was watching ends (Q10: no notification row).
  useEffect(() => {
    if (!run) return;
    const before = lastStatus.current;
    lastStatus.current = run.status;
    if (!before || isTerminalRunStatus(before) || !isTerminalRunStatus(run.status)) return;
    if (run.status === 'done') {
      toast(run.lint && !run.lint.ok ? `The draft is written with ${run.lint.count} problem${run.lint.count === 1 ? '' : 's'} to fix in the editor.` : 'The draft is written. Read it before you publish.');
    } else if (run.status === 'failed') {
      toast('The build stopped. The cause is on the page.', { type: 'error' });
    }
  }, [run, toast]);

  const header = (
    <PageHeader
      title="Course builder run"
      icon={Sparkles}
      subtitle={run ? MODE_WORDS[run.mode] ?? null : null}
      actions={
        <Link to={catalogPaths.list()} className={outlineBtnCls()}>
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Catalog
        </Link>
      }
    />
  );

  if (!run) {
    return (
      <div className="space-y-4" data-testid="lms-builder-run">
        {header}
        {error ? (
          <div className={noticeCls(error.code === 'LMS_BUILDER_DISABLED' ? 'amber' : 'red')} role="alert" data-testid="lms-builder-run-error">
            {explainError(error.code, error.message)}
          </div>
        ) : loading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400" role="status">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
            Loading the run...
          </div>
        ) : null}
      </div>
    );
  }

  const pill = statusPill(run.status);
  const sources = detail?.sources ?? [];
  const links = Array.isArray(run.targets?.source_links) ? run.targets.source_links : [];
  const linkUrls = new Set(links.map((l) => l.url));
  const estimateCents = run.estimate?.cents ?? run.estimate_cents;
  const progress = run.progress || {};
  const line = sectionLine(run);
  const warnings = Array.isArray(progress.warnings) ? progress.warnings.filter((w) => typeof w === 'string' && w) : [];
  const terminal = isTerminalRunStatus(run.status);
  const cancelAsked = Boolean(run.cancel_requested_at) && !terminal;

  const cancel = async () => {
    setBusy('cancel');
    setActionError(null);
    try {
      const next = await cancelBuilderRun(run.id);
      toast(next.status === 'cancelled' ? 'The run is cancelled.' : 'The run stops before its next AI call.', { type: 'info' });
      setConfirmCancel(false);
      refresh();
    } catch (err) {
      const problem = readBuilderError(err, 'The run could not be cancelled.');
      setActionError(explainError(problem.code, problem.message));
      setConfirmCancel(false);
    } finally {
      setBusy(null);
    }
  };

  const retry = async () => {
    setBusy('retry');
    setActionError(null);
    try {
      await startBuilderRun(run.id);
      toast('The build continues from where it stopped.', { type: 'info' });
      refresh();
    } catch (err) {
      const problem = readBuilderError(err, 'The run could not be restarted.');
      setActionError(explainError(problem.code, problem.message));
    } finally {
      setBusy(null);
    }
  };

  const openSource = async (source: BuilderSource) => {
    setLinkFor(null);
    const tab = openPendingTab();
    try {
      const { url } = await getBuilderSourceUrl(source.id);
      if (tab) tab.location.href = url;
      else setLinkFor({ id: source.id, url });
    } catch (err) {
      if (tab) tab.close();
      const problem = readBuilderError(err, 'The file could not be opened.');
      toast(problem.status === 404 ? 'That file was withdrawn or is no longer available.' : problem.message, { type: 'error' });
    }
  };

  const withdraw = async () => {
    if (!withdrawing || !withdrawReason.trim() || withdrawBusy) return;
    setWithdrawBusy(true);
    setWithdrawError(null);
    try {
      await withdrawBuilderSource(withdrawing.id, withdrawReason);
      toast(`${withdrawing.filename} is withdrawn: it is no longer listed or opened.`);
      setWithdrawing(null);
      setWithdrawReason('');
      refresh();
    } catch (err) {
      const problem = readBuilderError(err, 'The file could not be withdrawn.');
      setWithdrawError(explainError(problem.code, problem.message));
    } finally {
      setWithdrawBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="lms-builder-run" data-status={run.status}>
      {header}

      <div className="flex flex-wrap items-center gap-2">
        <span className={pillCls(pill.tone)} data-testid="lms-builder-run-status">
          {pill.label}
        </span>
        {cancelAsked ? <span className={pillCls('amber')}>Cancelling</span> : null}
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {`Started by ${run.requested_by?.full_name || 'an admin'}`}
          {run.targets?.title ? ` \u00b7 ${run.targets.title}` : ''}
        </span>
      </div>

      {run.status === 'done' ? (
        <div className={noticeCls(run.lint && !run.lint.ok ? 'amber' : 'green')} role="status" data-testid="lms-builder-run-done">
          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <div className="space-y-2">
            <p>
              {run.lint && !run.lint.ok
                ? `The draft is written. ${run.lint.count} problem${run.lint.count === 1 ? '' : 's'} to fix in the editor before it can be published.`
                : 'The draft is written and passes the checks. Read every slide and question before you publish it.'}
            </p>
            {run.version_id ? (
              <Link to={catalogPaths.version(run.version_id)} className={primaryBtnCls} data-testid="lms-builder-open-draft">
                <PencilLine className="h-4 w-4" aria-hidden="true" />
                Open the draft
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}

      {run.status === 'failed' ? (
        <div className={noticeCls('red')} role="alert" data-testid="lms-builder-run-failed">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <div className="space-y-2">
            <p data-testid="lms-builder-run-failure">{explainError(run.error_code, run.error)}</p>
            {run.stage ? <p className="text-xs">{`It stopped at: ${stageRows(run).find((r) => r.stage === run.stage)?.label ?? run.stage}${progress.section ? `, ${progress.section}` : ''}.`}</p> : null}
            <div className="flex flex-wrap gap-2">
              {canRetryRun(run) ? (
                <button type="button" className={primaryBtnCls} onClick={retry} disabled={busy !== null} data-testid="lms-builder-retry">
                  {busy === 'retry' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <RotateCcw className="h-4 w-4" aria-hidden="true" />}
                  Retry from where it stopped
                </button>
              ) : null}
              {run.version_id ? (
                <Link to={catalogPaths.version(run.version_id)} className={secondaryBtnCls} data-testid="lms-builder-open-partial">
                  <PencilLine className="h-4 w-4" aria-hidden="true" />
                  Open the partial draft
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {run.status === 'cancelled' ? (
        <div className={noticeCls('amber')} role="status" data-testid="lms-builder-run-cancelled">
          The run was cancelled. What it spent is kept in the records; nothing was published.
        </div>
      ) : null}

      {actionError ? (
        <div className={noticeCls('red')} role="alert" data-testid="lms-builder-action-error">
          {actionError}
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <section className={`${cardCls} space-y-3`} aria-labelledby="lms-builder-progress-title">
          <h3 id="lms-builder-progress-title" className={subheadingCls}>
            Progress
          </h3>
          <StageList run={run} />
        </section>

        <section className={`${cardCls} space-y-3 lg:col-span-2`} aria-label="Details" data-testid="lms-builder-run-details">
          {progress.message && !terminal ? (
            <p className="text-sm text-gray-800 dark:text-gray-200" aria-live="polite" data-testid="lms-builder-run-message">
              {progress.message}
            </p>
          ) : null}
          {line ? (
            <div className="space-y-1">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100" data-testid="lms-builder-section-line">
                {line}
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700" aria-hidden="true">
                <div className="h-full rounded-full bg-primary-600 dark:bg-primary-500" style={{ width: `${sectionPercent(run)}%` }} />
              </div>
            </div>
          ) : null}
          <dl className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <dt className={microLabelCls}>Spent so far</dt>
              <dd className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100" data-testid="lms-builder-spent">
                {formatCents(run.spent_cents)}
              </dd>
            </div>
            <div>
              <dt className={microLabelCls}>Estimate</dt>
              <dd className="text-sm tabular-nums text-gray-900 dark:text-gray-100">{estimateCents !== null && estimateCents !== undefined ? formatCents(estimateCents) : '\u2014'}</dd>
            </div>
            <div>
              <dt className={microLabelCls}>Repairs</dt>
              <dd className="text-sm tabular-nums text-gray-900 dark:text-gray-100" data-testid="lms-builder-repairs">
                {`${typeof progress.repairs_done === 'number' ? progress.repairs_done : 0}`}
              </dd>
            </div>
            <div>
              <dt className={microLabelCls}>Elapsed</dt>
              <dd className="text-sm tabular-nums text-gray-900 dark:text-gray-100" data-testid="lms-builder-elapsed">
                {formatElapsed(elapsedMs(run, now))}
              </dd>
            </div>
          </dl>
          {run.models?.author || run.models?.extract ? (
            <p className={`${hintCls} break-all`}>{`Models: writing ${run.models.author ?? '\u2014'}, planning ${run.models.extract ?? '\u2014'}`}</p>
          ) : null}
          {warnings.length > 0 ? (
            <div className={noticeCls('amber')} role="status" data-testid="lms-builder-warnings">
              <FileWarning className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <ul className="list-disc space-y-0.5 pl-4">
                {warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {!terminal ? (
            <div className="flex justify-end">
              <button
                type="button"
                className={outlineBtnCls()}
                onClick={() => setConfirmCancel(true)}
                disabled={busy !== null || cancelAsked}
                data-testid="lms-builder-cancel"
              >
                {cancelAsked ? 'Cancel requested' : 'Cancel the run'}
              </button>
            </div>
          ) : null}
        </section>
      </div>

      <section className="space-y-2" aria-labelledby="lms-builder-sources-title">
        <h3 id="lms-builder-sources-title" className={subheadingCls}>
          {`Sources (${sources.length})`}
        </h3>
        {sources.length === 0 && links.length === 0 ? <p className={hintCls}>This run has no files of its own.</p> : null}
        {sources.length > 0 ? (
          <ul className="space-y-2" data-testid="lms-builder-run-sources">
            {sources.map((s) => {
              const fromLink = Boolean(s.public_url && linkUrls.has(s.public_url));
              const withdrawn = Boolean(s.withdrawn_at);
              return (
                <li key={s.id} className={`${cardCls} flex flex-wrap items-start justify-between gap-2`} data-testid={`lms-builder-run-source-${s.ordinal}`}>
                  <div className="min-w-0 space-y-0.5">
                    <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{s.title || s.filename}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {fromLink ? `Web link: ${s.public_url}` : `${s.filename} \u00b7 ${fileSizeLabel(s.size_bytes)}`}
                      {typeof s.page_count === 'number' ? ` \u00b7 ${s.page_count} pages` : ''}
                    </div>
                    {s.extraction_note ? <div className="text-xs text-gray-600 dark:text-gray-300">{s.extraction_note}</div> : null}
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {withdrawn ? <span className={pillCls('gray')}>Withdrawn</span> : null}
                      {s.extraction_status === 'refused' || s.extraction_status === 'failed' ? (
                        <span className={pillCls('red')}>{s.extraction_status === 'refused' ? 'Refused' : 'Could not be read'}</span>
                      ) : s.extraction_status === 'done' ? (
                        <span className={pillCls('green')}>Read</span>
                      ) : (
                        <span className={pillCls('gray')}>Not read yet</span>
                      )}
                    </div>
                    {linkFor && linkFor.id === s.id ? (
                      <a href={linkFor.url} target="_blank" rel="noopener noreferrer" className={`text-xs ${linkCls}`} data-testid="lms-builder-source-fallback">
                        Your browser blocked the new tab: open the file here
                      </a>
                    ) : null}
                  </div>
                  {!withdrawn ? (
                    <div className="flex flex-wrap gap-2">
                      <button type="button" className={outlineBtnCls()} onClick={() => openSource(s)} data-testid={`lms-builder-source-open-${s.ordinal}`}>
                        <ExternalLink className="h-4 w-4" aria-hidden="true" />
                        Open
                      </button>
                      {terminal ? (
                        <button
                          type="button"
                          className={outlineBtnCls()}
                          onClick={() => {
                            setWithdrawing(s);
                            setWithdrawReason('');
                            setWithdrawError(null);
                          }}
                          data-testid={`lms-builder-source-withdraw-${s.ordinal}`}
                        >
                          Withdraw this file
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
        {links.length > 0 ? (
          <div className="space-y-1" data-testid="lms-builder-run-links">
            <div className={microLabelCls}>{`Web links (${links.length})`}</div>
            <ul className="space-y-1">
              {links.map((link, i) => {
                const outcome = linkOutcome(link, sources, run);
                return (
                  <li key={link.url} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`lms-builder-run-link-${i}`}>
                    <span className={pillCls(outcome.tone)}>{outcome.label}</span>
                    <span className="min-w-0 break-all text-gray-800 dark:text-gray-200">{link.title ? `${link.title} \u00b7 ${link.url}` : link.url}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </section>

      <ConfirmModal
        isOpen={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        onConfirm={cancel}
        title="Cancel this run?"
        message="The builder stops before its next AI call. What it has spent is kept in the records, and nothing is published. A cancelled run cannot be restarted."
        confirmLabel="Cancel the run"
        cancelLabel="Keep building"
        danger
        loading={busy === 'cancel'}
      />

      <Modal
        isOpen={withdrawing !== null}
        onClose={withdrawBusy ? () => {} : () => setWithdrawing(null)}
        title={withdrawing ? `Withdraw ${withdrawing.filename}?` : 'Withdraw the file?'}
        maxWidth="max-w-lg"
      >
        <div className="space-y-3" data-testid="lms-builder-withdraw-dialog">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            The file stops being listed and opened, and this run cannot be retried. The stored copy stays in the Learning Center's bucket until the platform owner removes it by hand (the launch runbook has the steps). Drafts built from it keep their source notes, marked withdrawn.
          </p>
          <TextArea label="Reason (required)" value={withdrawReason} onChange={setWithdrawReason} rows={2} testId="lms-builder-withdraw-reason" />
          {withdrawError ? (
            <div className={noticeCls('red')} role="alert">
              {withdrawError}
            </div>
          ) : null}
          <div className="flex justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
            <button type="button" className={secondaryBtnCls} onClick={() => setWithdrawing(null)} disabled={withdrawBusy}>
              Keep the file
            </button>
            <button type="button" className={dangerBtnCls} onClick={withdraw} disabled={!withdrawReason.trim() || withdrawBusy} data-testid="lms-builder-withdraw-confirm">
              {withdrawBusy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Withdraw
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export interface SourceOpenPageProps {
  /** Where the browser goes with the presigned address (tests inject it). */
  openUrl?: (url: string) => void;
}

function defaultOpenUrl(url: string): void {
  window.location.replace(url);
}

/** The platform link of an uploaded source (7.3): fetch the presigned address and go there. */
export function SourceOpenPage({ openUrl = defaultOpenUrl }: SourceOpenPageProps) {
  const { sourceId } = useParams();
  const [state, setState] = useState<{ url: string; filename: string } | { error: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!sourceId) {
      setState({ error: 'No file was named.' });
      return undefined;
    }
    getBuilderSourceUrl(sourceId)
      .then((answer) => {
        if (cancelled) return;
        setState(answer);
        openUrl(answer.url);
      })
      .catch((err) => {
        if (cancelled) return;
        const problem = readBuilderError(err, 'The file could not be opened.');
        setState({
          error:
            problem.status === 404
              ? 'This file was withdrawn or is no longer available.'
              : explainError(problem.code, problem.message),
        });
      });
    return () => {
      cancelled = true;
    };
    // openUrl is a stable default or a test double.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId]);

  return (
    <div className="space-y-4" data-testid="lms-builder-source-page">
      <PageHeader title="Course source" icon={Sparkles} />
      {state === null ? (
        <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400" role="status">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Opening the file...
        </div>
      ) : 'error' in state ? (
        <div className={noticeCls('amber')} role="alert" data-testid="lms-builder-source-error">
          {state.error}
        </div>
      ) : (
        <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="lms-builder-source-opening">
          {`Opening ${state.filename}. If nothing happens, `}
          <a href={state.url} className={linkCls} rel="noopener noreferrer">
            open the file here
          </a>
          .
        </p>
      )}
      <Link to={catalogPaths.list()} className={`inline-flex items-center gap-1 text-sm ${linkCls}`}>
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Catalog
      </Link>
    </div>
  );
}

export default RunPage;
